/* eslint-disable react/display-name */
/* eslint-disable @typescript-eslint/no-unused-vars */
import rowStyles from '@/components/StoreProductsTable/TableRow.module.css';
import { Modifier } from '@/types/menus.types';
import { Box, Grid } from '@mui/material';
import { CellContext } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import FZMenuModifierTable from './FZMenuModifierTable';
import styles from './FZStoreModifierItemTable.module.css';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { FZStoreModifierItemTableProps } from '@/components/ModifiersTable/ModifiersTable.types';
import { formatModifierPriceCell } from '@/pages/Franchisee/Menus/StoreMenuModifiersDetails/modifierDisplay.helpers';

const CellModifierDescription = ({ row }: CellContext<Modifier, unknown>) => {
  const { modifierName, modifierDescription } = row.original;

  return (
    <Grid container direction="column" justifyContent="center" alignItems="flex-start" width="100%" minWidth="0">
      <Grid item className={styles.description}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>{modifierName}</div>
      </Grid>
      <Grid item className={styles.subDescription}>
        <span className={styles.productDescription}>{modifierDescription}</span>
      </Grid>
    </Grid>
  );
};

function getFirstEightChars(header: string): string {
  return header.substring(0, 8);
}

const getPriceDescription = (
  header: string,
  inactiveLabel: string,
  modifierCategory: string,
  setPriceDeviationsData: FZStoreModifierItemTableProps['setPriceDeviationsData'],
) => {
  return ({ row }: CellContext<Modifier, unknown>) => {
    const { storeGroups, modifierId, slotId, modifierWeightId, variantId } = row.original;
    const storeGroup = storeGroups.find((sg) => sg.storeGroupName === header);
    const { text, showRangeInfo } = formatModifierPriceCell(storeGroup?.chosenPriceRange, inactiveLabel);

    return showRangeInfo ? (
      <div className={styles.rangePriceCell}>
        <div className={styles.noWrapPrice}>{text}</div>
        <InfoOutlined
          className={styles.priceRangeInfo}
          sx={{
            width: '22px',
            height: '22px',
            color: '#266DCC',
            cursor: 'pointer',
          }}
          onClick={() =>
            setPriceDeviationsData({
              isPriceDeviationsDrawerOpen: true,
              selectedGroupName: header,
              modifierCategory,
              modifierKeys: [
                {
                  modifierId,
                  slotId,
                  modifierWeightId,
                  variantId: variantId as string,
                },
              ],
              cameFromEditDrawer: false,
            })
          }
        />
      </div>
    ) : (
      <span className={styles.noWrapPrice}>{text}</span>
    );
  };
};

const FZStoreModifierItemTable = (props: FZStoreModifierItemTableProps) => {
  const {
    changeHeaderSize,
    menuModifiers,
    dynamicHeaders,
    selectedRows,
    onRowSelectionChange,
    isLoading,
    storesAndGroups,
    isFranchiseeEditAndFreeze,
    setPriceDeviationsData,
    modifierCategory,
  } = props;

  const { t } = useTranslation();

  const getStoreGroupsCount = useCallback(
    (header: string) => {
      let total = 0;
      let count = 0;
      if (storesAndGroups.origin === 'tab-store') {
        total = storesAndGroups.countByStoreGroup?.find((sg) => sg.storeGroup === header)?.count as number;
        count = storesAndGroups?.stores?.filter((store) => store.storeGroup === header).length as number;
      }

      if (storesAndGroups.origin === 'tab-stores-groups') {
        total = storesAndGroups?.storeGroups?.find((sg) => sg.storeGroup === header)?.storeCount as number;
        count = storesAndGroups?.storeGroups?.find((sg) => sg.storeGroup === header)?.stores.length as number;
      }

      const headerWithFistEightChars = getFirstEightChars(header);

      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
          <strong>{headerWithFistEightChars}</strong>
          <span>
            {count} / {total}
          </span>
        </div>
      );
    },
    [storesAndGroups],
  );

  const columns = useMemo(() => {
    const inactiveLabel = t('common.inactive');
    const lColumns = [
      {
        header: t('menuDetails.storeItem.table.modifier'),
        accessorKey: 'modifierDescription',
        enableSorting: true,
        cell: CellModifierDescription,
        sortingFn: 'sortModifier',
        columnClassname: 'productHeaderStyle',
      } as any,
    ];
    dynamicHeaders.forEach((header, index) => {
      lColumns.push({
        header: getStoreGroupsCount(header),
        accessorKey: header,
        columnClassname:
          index < dynamicHeaders.length - 1
            ? dynamicHeaders.length < 6
              ? 'dynamicHeader150'
              : 'dynamicHeader'
            : 'dynamicHeaderLastItem',
        enableSorting: true,
        cell: getPriceDescription(header, inactiveLabel, modifierCategory, setPriceDeviationsData),
        sortingFn: 'sortModifierByPrice',
      } as any);
    });

    return lColumns;
  }, [t, dynamicHeaders, getStoreGroupsCount, modifierCategory, setPriceDeviationsData]);

  return (
    <div className={rowStyles.tablePage}>
      <div>
        <Box>
          <FZMenuModifierTable
            returnedRecordsText={(count) => t('menu.table.returnedRecord', { count })}
            data={menuModifiers}
            isLoading={isLoading}
            enableRowSelection
            columns={columns}
            selectedRows={selectedRows}
            onRowSelectionChange={onRowSelectionChange}
            changeHeaderSize={changeHeaderSize}
            isFranchiseeEditAndFreeze={isFranchiseeEditAndFreeze}
          />
        </Box>
      </div>
    </div>
  );
};

export default FZStoreModifierItemTable;
